<!-- DashboardADMIN.vue -->
<template>
  <div class="cl-page">
    <div class="cl-pageheader">
      <div class="header-content">
        <div class="cl-pageheader-title">ADMIN Report Management</div>
        <div class="header-controls">
          <button class="button_addreport" @click="openModal">Add Report</button>
          <v-select 
            label="Filter: League/Season" 
            v-model="seasonFilter" 
            :items="seasonOptions"
            class="select_leagueseason" 
            density="compact" 
            hide-details 
            clearable
          />
        </div>
      </div>
    </div>

  <!-- Loading overlay -->
  <v-overlay :model-value="isLoading" class="align-center justify-center" persistent>
    <div class="d-flex flex-column align-center ga-2">
      <v-progress-circular indeterminate size="40" />
      <div>Loading data…</div>
    </div>
  </v-overlay>

  <!-- Reports table -->
  <v-container class="pt-2 gridContainer">
    <v-card rounded="lg" elevation="2">
      <v-data-table
        :headers="responsiveHeaders"
        :items="matchreports"
        :density="tableDensity"
        item-key="id"
        hover
        class="elevation-0"
        fixed-header
        :height="tableHeight"
        :items-per-page="-1"
        hide-default-footer
        @click:row="onRowClick"
        :mobile="isMobile"
      >
        <template v-slot:[`item.fullname`]="{ item }">
          <div class="cell-left">{{ item.fullname }}</div>
        </template>

        <template v-slot:[`item.GameDate`]="{ item }">
          <div class="cell-left">{{ item.GameDate }}</div>
        </template>

        <template v-slot:[`item.GameNumber`]="{ item }">
          <div class="cell-left">{{ item.GameNumber }}</div>
        </template>

        <template v-slot:[`item.LeagueTypeSeason`]="{ item }">
          <div class="cell-left">{{ item.LeagueTypeSeason }}</div>
        </template>

        <template v-slot:[`item.GenderAge`]="{ item }">
          <div class="cell-left">{{ item.GenderAge }}</div>
        </template>

        <template v-slot:[`item.teams`]="{ item }">
          <div class="cell-left">{{ item.TeamHome }} vs. {{ item.TeamAway }}</div>
        </template>

        <template v-slot:[`item.YellowCardCount`]="{ item }">
          <v-chip
              v-if="item.YellowCardCount > 0"
              color="yellow"
              text-color="black"
              :size="chipSize"
              variant="flat"
              label
            >
              {{ item.YellowCardCount }}
            </v-chip>
        </template>
        <template v-slot:[`item.RedCardCount`]="{ item }">
          <v-chip
            v-if="item.RedCardCount > 0"
            color="red"
            :size="chipSize"
            variant="flat"
            label
          >
            {{ item.RedCardCount }}
          </v-chip>
        </template>
        <template v-slot:[`item.WarnCount`]="{ item }">
          <v-chip
              v-if="item.WarnCount > 0"
              color="orange"
              text-color="black"
              :size="chipSize"
              variant="flat"
              label
            >
              {{ item.WarnCount }}
            </v-chip>
        </template>
        <template v-slot:[`item.RemovalCount`]="{ item }">
          <v-chip
            v-if="item.RemovalCount > 0"
            color="red-darken-4"
            text-color="white"
            :size="chipSize"
            variant="flat"
            label
          >
            {{ item.RemovalCount }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex ga-1">
            <v-btn
              icon="mdi-pencil"
              :size="actionButtonSize"
              variant="text"
              @click.stop="editMatchReport(item.id)"
            />
            <v-btn
              icon="mdi-delete"
              :size="actionButtonSize"
              variant="text"
              color="error"
              @click.stop="deleteMatchReport(item.id)"
            />
          </div>
        </template>

        <template #bottom>
          <div class="d-flex justify-center py-3">
            <v-btn
              v-if="hasMore"
              color="primary"
              variant="tonal"
              :size="loadMoreButtonSize"
              @click="loadMatchReports()"
            >
              Load more
            </v-btn>
            <div v-else-if="matchreports.length" class="text-medium-emphasis text-caption">
              End of results
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>

  <!-- Create report dialog -->
  <v-dialog v-model="showModal" :max-width="dialogMaxWidth">
    <v-card>
      <v-card-title class="text-h6">Create match report for the following Referee</v-card-title>
      <v-card-text>
        <v-autocomplete
          v-model="selectedUserId"
          :items="users"
          item-title="fullname"
          item-value="id"
          label="Select Referee"
          clearable
          chips
          :density="selectDensity"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="cancelModal">Cancel</v-btn>
        <v-btn color="primary" :disabled="!selectedUserId" @click="addMatchReport">Submit</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script setup>
import { db, matchreportsColRef, misconductColRef } from "@/firebase";
import { collection, query, orderBy, getDocs, deleteDoc, doc, where, limit, startAfter } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useRouter } from "vue-router";
import { ref, onMounted, onUnmounted, computed, watch } from "vue";

// If you don't already export usersColRef from @/firebase,
// we create it from db here to avoid any dependency mismatch.
const usersColRef = collection(db, "users");

// ---------- State ----------
const router = useRouter();

const isLoading = ref(true);
const showModal = ref(false);

const matchreports = ref([]);
const lastVisible = ref(null);
const hasMore = ref(false);
const PAGE_SIZE = 50;

const users = ref([]);            // dialog list
const usersMap = ref(new Map());  // Map<uid, displayName>
const selectedUserId = ref(null);

// Responsive computed properties
const isMobile = ref(false);
const isTablet = ref(false);

// Base headers configuration
const baseHeaders = [
  { title: "Name", key: "fullname", width: 180, align: "start" },
  { title: "Date", key: "GameDate", width: 110, align: "start" },
  { title: "Game #", key: "GameNumber", width: 100, align: "start" },
  { title: "LeagueTypeSeason", key: "LeagueTypeSeason", width: 250, align: "start" },
  { title: "GenderAge", key: "GenderAge", width: 120, align: "start" },
  { title: "Teams", key: "teams", width: 350, align: "start" },
  { title: "YC", key: "YellowCardCount", width: 50, align: "center" },
  { title: "RC", key: "RedCardCount", width: 50, align: "center" },
  { title: "WARN", key: "WarnCount", width: 70, align: "center" },
  { title: "REMOVAL", key: "RemovalCount", width: 90, align: "center" },
  { title: "Actions", key: "actions", sortable: false, width: 120 },
];

const mobileHeaders = [
  { title: "Name", key: "fullname", width: 120, align: "start" },
  { title: "Date", key: "GameDate", width: 80, align: "start" },
  { title: "Teams", key: "teams", width: 200, align: "start" },
  { title: "Cards", key: "WarnCount", width: 100, align: "center" },
  { title: "Actions", key: "actions", sortable: false, width: 80 },
];

const tabletHeaders = [
  { title: "Name", key: "fullname", width: 150, align: "start" },
  { title: "Date", key: "GameDate", width: 90, align: "start" },
  { title: "Game #", key: "GameNumber", width: 70, align: "start" },
  { title: "League", key: "LeagueTypeSeason", width: 180, align: "start" },
  { title: "Teams", key: "teams", width: 250, align: "start" },
  { title: "Cards", key: "WarnCount", width: 120, align: "center" },
  { title: "Actions", key: "actions", sortable: false, width: 100 },
];

// Responsive headers
const responsiveHeaders = computed(() => {
  if (isMobile.value) return mobileHeaders;
  if (isTablet.value) return tabletHeaders;
  return baseHeaders;
});

// Responsive properties
const tableDensity = computed(() => isMobile.value ? 'compact' : 'comfortable');
const tableHeight = computed(() => {
  if (isMobile.value) return '60vh';
  if (isTablet.value) return '70vh';
  return '80vh';
});

const chipSize = computed(() => isMobile.value ? 'x-small' : 'small');
const actionButtonSize = computed(() => isMobile.value ? 'x-small' : 'small');
const loadMoreButtonSize = computed(() => isMobile.value ? 'small' : 'default');
const selectDensity = computed(() => isMobile.value ? 'compact' : 'comfortable');
const dialogMaxWidth = computed(() => isMobile.value ? '90vw' : '520px');

const seasonFilter = ref('All');

// Build options from currently loaded rows (reactive)
const seasonOptions = computed(() => {
  const set = new Set((matchreports.value || [])
    .map(r => r?.LeagueTypeSeason)
    .filter(Boolean));
  return ['All', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
});

// When filter changes, reload from page 1
watch(seasonFilter, () => loadMatchReports(true));

// ---------- Responsive Detection ----------
const checkScreenSize = () => {
  isMobile.value = window.innerWidth < 768;
  isTablet.value = window.innerWidth >= 768 && window.innerWidth < 1024;
};

// ---------- Lifecycle ----------
onMounted(() => {
  checkScreenSize();
  window.addEventListener('resize', checkScreenSize);
  onInit();
});

onUnmounted(() => {
  window.removeEventListener('resize', checkScreenSize);
});

async function onInit() {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    router.push("/login");
    return;
  }

  // ✅ FIX: Validate user access before loading data
  try {
    await validateUserAccess(user);
  } catch (error) {
    console.error("Access validation failed:", error);
    alert(error.message);
    router.push("/login");
    return;
  }

  try {
    await loadUsers();
    await loadMatchReports(true);
  } finally {
    isLoading.value = false;
  }
}

// ✅ NEW: Validate user has proper access
async function validateUserAccess(user) {
  // Force refresh token to get latest claims
  await user.getIdToken(true);
  const idTokenResult = await user.getIdTokenResult(true);
  const claims = idTokenResult.claims;

  console.log("DashboardADMIN - Validating claims:", claims);

  // Check if claims exist
  if (!claims || !claims.orgRoles) {
    throw new Error("No access roles found. Please contact administrator.");
  }

  // Get stored org and role from localStorage
  const storedOrg = localStorage.getItem("currentUserOrgName");
  const storedRole = localStorage.getItem("currentUserRole");

  if (!storedOrg || !storedRole) {
    throw new Error("Session data missing. Please log in again.");
  }

  // Validate user has access to the stored organization
  const rolesMap = claims.orgRoles || {}; // Use orgRoles from new claims structure
  const claimsRolesForOrg = rolesMap[storedOrg];

  if (!claimsRolesForOrg || claimsRolesForOrg.length === 0) {
    const availableOrgs = Object.keys(rolesMap).join(", ") || "(none)";
    throw new Error(
      `You no longer have access to "${storedOrg}". Available organizations: ${availableOrgs}`
    );
  }

  // ✅ Validate user selected ADMIN role
  if (storedRole !== "ADMIN") {
    throw new Error(`This dashboard requires ADMIN role. You selected "${storedRole}".`);
  }

  console.log(`✅ User selected ADMIN role, Available roles in org: ${claimsRolesForOrg.join(", ")}`);
  
  // Note: If claims don't match, it's okay - they will be updated on next login
  if (!claimsRolesForOrg.includes(storedRole)) {
    console.log(`ℹ️ Claims will be updated to match selected role on next token refresh`);
  }

  // ✅ Check token expiration
  const expirationTime = new Date(idTokenResult.expirationTime);
  const now = new Date();
  const timeUntilExpiry = expirationTime - now;

  console.log(`Token expires in ${Math.floor(timeUntilExpiry / 1000 / 60)} minutes`);

  // If token expires in less than 5 minutes, refresh it
  if (timeUntilExpiry < 5 * 60 * 1000) {
    console.log("Token expiring soon, refreshing...");
    await user.getIdToken(true);
  }

  return true;
}

// ---------- Helpers ----------
function getCurrentUserOrgName() {
  const orgName = localStorage.getItem("currentUserOrgName");
  if (!orgName) console.error("Organization name missing in localStorage");
  return orgName;
}

function toDateSafe(gd) {
  if (!gd) return "N/A";
  const d = typeof gd?.toDate === "function" ? gd.toDate() : gd;
  try {
    return d instanceof Date ? d.toLocaleDateString() : "N/A";
  } catch {
    return "N/A";
  }
}

// ---------- Load users (users collection) ----------
async function loadUsers() {
  const adminOrg = getCurrentUserOrgName();
  if (!adminOrg) return;

  // 1) Fetch all users (or add a denormalized index later; see note below)
  const snap = await getDocs(usersColRef);

  // 2) Keep only users whose Roles array has { Organization: adminOrg, Role: 'Referee' }
  const filteredDocs = snap.docs.filter(d => {
    const roles = d.data()?.Roles;
    return Array.isArray(roles) && roles.some(r =>
      r?.Organization === adminOrg && r?.Role === 'Referee'
    );
  });

  // 3) Build list for the dialog and a map for name joins
  const m = new Map();
  const list = filteredDocs.map(d => {
    const u = d.data() || {};
    const first = (u.NameFirst ?? '').trim();
    const last  = (u.NameLast  ?? '').trim();
    const fullname = (first || last) ? `${first} ${last}`.trim()
                    : (u.displayName || u.email || 'Unknown');

    // match either stored uid/ID or doc id
    const idFromDoc = d.id;
    const idFromFld = u.ID || u.uid;

    if (idFromDoc) m.set(idFromDoc, fullname);
    if (idFromFld) m.set(idFromFld, fullname);

    return { id: idFromFld || idFromDoc, fullname, last, org: adminOrg };
  });

  list.sort((a, b) => a.last.localeCompare(b.last));
  users.value    = list;  // for <v-autocomplete item-title="fullname" item-value="id">
  usersMap.value = m;     // for joining names in reports
}

// ---------- Load match reports (paginated) ----------
async function loadMatchReports(reset = false) {
  const adminOrg = getCurrentUserOrgName();
  if (!adminOrg) return;

  if (reset) {
    matchreports.value = [];
    lastVisible.value = null;
    hasMore.value = false;
  }

  const filters = [where("Organization", "==", adminOrg)];
  if (seasonFilter.value && seasonFilter.value !== 'All') {
    filters.push(where("LeagueTypeSeason", "==", seasonFilter.value));
  }

  let qBase = query(matchreportsColRef, ...filters, orderBy("GameDate", "desc"), limit(PAGE_SIZE));

  if (lastVisible.value) {
    qBase = query(matchreportsColRef, ...filters, orderBy("GameDate", "desc"), startAfter(lastVisible.value), limit(PAGE_SIZE));
  }

  const snap = await getDocs(qBase);
  if (snap.empty) {
    hasMore.value = false;
    lastVisible.value = null;
    return;
  }

  lastVisible.value = snap.docs[snap.docs.length - 1];

  const rows = snap.docs.map(function mapDoc(docSnap) {
    const data = docSnap.data();
    const fullname = usersMap.value.get(data.UserID) || "Unknown";
    return {
      id: docSnap.id,
      ...data,
      fullname,
      GameDate: toDateSafe(data.GameDate),
    };
  });

  matchreports.value = [...matchreports.value, ...rows];
  hasMore.value = snap.size === PAGE_SIZE;
  lastVisible.value = hasMore.value ? snap.docs[snap.docs.length - 1] : null;
}

// ---------- Dialog controls ----------
async function openModal() {
  if (!users.value.length) await loadUsers();
  showModal.value = true;
}

function cancelModal() {
  showModal.value = false;
  selectedUserId.value = null;
}

function onRowClick(_evt, row) {
  // Vuetify 3: the original item is at row.item.raw
  const id = row?.item?.raw?.id ?? row?.item?.id ?? row?.raw?.id ?? row?.id;
  if (id) editMatchReport(id);
}

// ---------- Actions ----------
function editMatchReport(id) {
  router.push({
    name: "MatchReport",
    params: { id },
    query: { isNew: false },
  });
}

async function addMatchReport() {
  if (!selectedUserId.value) {
    alert("Please select a referee.");
    return;
  }

  const sel = users.value.find((u) => u.id === selectedUserId.value);
  const selectedUserOrg = sel?.org || getCurrentUserOrgName();

  showModal.value = false;
  console.log("Creating new report for user ID:", selectedUserId.value, "Org:", selectedUserOrg); 
  router.push({
    name: "MatchReport",
    params: { id: "NEW" },
    query: {
      selectedUserId: selectedUserId.value,
      selectedUserOrg,
      isNew: true,
    },
  });
}

async function deleteMatchReport(id) {
  if (!confirm("Are you sure you want to delete this report?")) return;

  try {
    // delete child misconduct first
    const qMisc = query(misconductColRef, where("MatchReportID", "==", id));
    const miscSnap = await getDocs(qMisc);
    if (!miscSnap.empty) {
      const deletions = miscSnap.docs.map((d) => deleteDoc(doc(db, "misconduct", d.id)));
      await Promise.all(deletions);
    }
    // then delete parent
    await deleteDoc(doc(db, "matchreports", id));
    matchreports.value = matchreports.value.filter((r) => r.id !== id);
  } catch (e) {
    console.error("Error deleting report (and children):", e);
  }
}
</script>

<style scoped>
/* Page - Responsive */
.cl-page {
  min-height: 100vh;
  background: radial-gradient(1200px 600px at 20% 0%, #eac891 0%, #eac891 40%, #f2d9ad 100%);
  padding-top: 80px; /* Account for any top navigation */
}

/* Header - Responsive */
.cl-pageheader {
  position: sticky;
  top: 0;
  height: auto;
  min-height: 60px;
  width: 100%;
  padding: clamp(10px, 2vw, 20px);
  border-top: 5px solid #02b8cc;
  border-bottom: 1px solid #32e3f7;
  background-color: #e78d58;
  color: rgb(0, 0, 0);
  z-index: 100;
  box-sizing: border-box;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
}

.cl-pageheader-title {
  color: rgb(0, 0, 0);
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  font-weight: bold;
  text-align: center;
  margin-bottom: 5px;
}

.header-controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  width: 100%;
}

/* Button - Responsive */
.button_addreport {
  height: 40px;
  min-width: 120px;
  max-width: 200px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 20px;
  border-radius: 12px;
  background-color: #99cc00;
  box-shadow: 0 4px 8px rgba(0,0,0,0.2), 0 6px 20px rgba(0,0,0,0.19);
  font-size: clamp(0.9rem, 2vw, 1rem);
  color: #000099;
  vertical-align: middle;
  border: none;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.button_addreport:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0,0,0,0.3), 0 8px 25px rgba(0,0,0,0.25);
}

/* Select - Responsive */
.select_leagueseason {
  width: 100%;
  max-width: 300px;
  min-width: 200px;
  z-index: 2;
}

/* Grid Container - Responsive */
.gridContainer {
  margin: 20px auto !important;
  padding: 0 !important;
  overflow-y: auto;
  scroll-behavior: smooth;
  font-size: 14px;
  height: calc(100vh - 240px); /* Dynamic height based on viewport */
  max-width: 1600px !important; /* Maximum width of grid container */
  width: 100% !important;
}

/* Table styling overrides */
:deep(.v-data-table .text-start) {
  text-align: left !important;
}

:deep(.cell-left) {
  text-align: left !important;
}

/* Responsive table adjustments */
:deep(.v-data-table) {
  font-size: clamp(11px, 2vw, 13px);
}

:deep(.v-data-table-header) {
  font-weight: 600;
}

:deep(.v-data-table-row:hover) {
  background-color: #f5f5f5 !important;
}

/* Dialog responsive adjustments */
:deep(.v-dialog) {
  margin: 20px;
}

:deep(.v-card-text) {
  padding: 20px;
}

:deep(.v-card-title) {
  padding: 20px 20px 10px 20px;
  font-size: clamp(1rem, 2.5vw, 1.25rem);
}

/* Mobile specific adjustments */
@media (max-width: 767px) {
  .header-content {
    gap: 10px;
  }
  
  .cl-pageheader-title {
    font-size: 1.2rem;
    margin-bottom: 0;
  }
  
  .header-controls {
    gap: 8px;
  }
  
  .button_addreport {
    height: 35px;
    font-size: 0.9rem;
  }
  
  .select_leagueseason {
    min-width: 150px;
  }
  
  .gridContainer {
    margin: 10px;
    height: calc(100vh - 260px);
  }
  
  :deep(.v-data-table) {
    font-size: 11px;
  }
  
  :deep(.v-chip) {
    font-size: 10px;
    height: 20px;
  }
  
  :deep(.v-btn--icon) {
    width: 28px;
    height: 28px;
  }
}

/* Tablet specific adjustments */
@media (min-width: 768px) and (max-width: 1023px) {
  .header-content {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
  }
  
  .cl-pageheader-title {
    text-align: left;
    margin-bottom: 0;
  }
  
  .header-controls {
    flex-direction: row;
    gap: 15px;
    align-items: center;
    width: auto;
  }
  
  .button_addreport {
    width: auto;
    min-width: 120px;
    max-width: 150px;
  }
  
  .select_leagueseason {
    width: 250px;
    min-width: 200px;
  }
}

/* Desktop specific adjustments */
@media (min-width: 1024px) {
  .header-content {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 30px;
  }
  
  .cl-pageheader-title {
    text-align: left;
    margin-bottom: 0;
  }
  
  .header-controls {
    flex-direction: row;
    gap: 20px;
    align-items: center;
    width: auto;
  }
  
  .button_addreport {
    width: auto;
    min-width: 150px;
    max-width: 200px;
  }
  
  .select_leagueseason {
    width: 260px;
    min-width: 220px;
  }
}

/* Large desktop optimizations */
@media (min-width: 1440px) {
  .gridContainer {
    max-width: 1400px;
    margin: 20px auto;
  }
}

/* Print styles */
@media print {
  .cl-page {
    background: white;
    padding-top: 0;
  }
  
  .cl-pageheader {
    background: white;
    border: 1px solid black;
    color: black;
    position: static;
  }
  
  .button_addreport {
    display: none;
  }
  
  .select_leagueseason {
    display: none;
  }
  
  .gridContainer {
    overflow: visible;
    max-height: none;
    margin: 10px;
  }
  
  :deep(.v-data-table) {
    font-size: 12px;
  }
  
  :deep(.v-data-table-row:hover) {
    background-color: white !important;
  }
}

/* High DPI screens */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  :deep(.v-btn) {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}

/* Accessibility improvements */
:deep(.v-btn:focus-visible) {
  outline: 2px solid #005ce6;
  outline-offset: 2px;
}

:deep(.v-select:focus-visible) {
  outline: 2px solid #005ce6;
  outline-offset: 2px;
}

/* Loading overlay responsive improvements */
:deep(.v-overlay__content) {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

:deep(.v-progress-circular) {
  margin-bottom: 10px;
}

/* Card type styling for mobile view */
@media (max-width: 767px) {
  .mobile-cards-summary {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .mobile-cards-summary :deep(.v-chip) {
    margin: 1px;
  }
}
</style>
